/** Approximate a circle as a GeoJSON polygon for Mapbox fill + dashed stroke. */
export function circlePolygon(
  lat: number,
  lng: number,
  radiusMiles: number,
  points = 64
): {
  type: 'Feature';
  properties: Record<string, never>;
  geometry: { type: 'Polygon'; coordinates: [number, number][][] };
} {
  const coords: [number, number][] = [];
  const earthRadiusMiles = 3958.7613;
  const angular = radiusMiles / earthRadiusMiles;
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;

  for (let i = 0; i <= points; i++) {
    const bearing = (i / points) * 2 * Math.PI;
    const destLat = Math.asin(
      Math.sin(latRad) * Math.cos(angular) +
        Math.cos(latRad) * Math.sin(angular) * Math.cos(bearing)
    );
    const destLng =
      lngRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(latRad),
        Math.cos(angular) - Math.sin(latRad) * Math.sin(destLat)
      );
    coords.push([(destLng * 180) / Math.PI, (destLat * 180) / Math.PI]);
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coords] },
  };
}

/** Zoom so ~3.4× diameter of the radius fits — leaves padding around the shade. */
export function zoomForRadiusMiles(lat: number, radiusMiles: number): number {
  const r = Math.max(radiusMiles, 0.25);
  const latRad = (lat * Math.PI) / 180;
  const milesPerDegree = 69 * Math.cos(latRad);
  const degrees = (r * 3.4) / Math.max(milesPerDegree, 0.01);
  const zoom = Math.log2(360 / degrees) - 1.15;
  return Math.max(7.5, Math.min(13, zoom));
}

/** Pixel radius for a miles circle at a Mapbox zoom (CSS pixels). */
export function radiusMilesToPixels(lat: number, radiusMiles: number, zoom: number): number {
  const metersPerPixel =
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return 0;
  return (Math.max(radiusMiles, 0) * 1609.344) / metersPerPixel;
}
